import {
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Hidden,
  InputLabel,
  TableCell,
  TableRow,
} from '@material-ui/core';
import catalogueApi, { SaleType } from 'api/query/catalogue';
import { Catalogue } from 'api/query/catalogue/catalogue';
import CatalogueApi from 'api/query/catalogue/catalogue-api';
import { CatalogueSort } from 'api/query/catalogue/catalogue-sort';
import { CatalogueSortBy } from 'api/query/catalogue/catalogue-sort-by.enum';
import { CatalogueSortDir } from 'api/query/catalogue/catalogue-sort-dir.enum';
import { CatalogueType } from 'api/query/catalogue/catalogue-type.enum';
import Axios, { CancelTokenSource } from 'axios';
import CascadingMenu, { CascadingMenuItem } from 'components/cascading-menu';
import Autocomplete from 'components/dropdown/autocomplete';
import Table from 'components/table';
import { Column } from 'components/table/column';
import TableFade from 'components/table/table-fade';
import deepEqual from 'deep-equal';
import moment from 'moment';
import { ResultsNumConstants } from 'pages/search/results/results-view/results-num-constants.enum';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigation } from 'react-navi';
import { getBreederAdmin } from '../../../api/query/query-utils';
import AuthorizedContent from '../../../components/authorized-content';
import { Dropdown } from '../../../components/dropdown/dropdown';
import { DropdownItem } from '../../../components/dropdown/dropdown-item';
import { UserRole } from '../../../providers/user';
import { addToStorage, StorageKey } from '../../../utils/storage-utils';
import { isNullOrUndefined } from '../../../utils/utils';
import { IndividualConstants } from '../../search/filters/individual/individual-constants.enum';
import { useFilters } from '../../search/filters/use-filters';
import { databaseTypes, getDefaultDatabase } from '../../search/prime/prime-utils';
import { usePrime } from '../../search/prime/use-prime';
import BackToDashboard from '../../shared/back-to-dashboard';
import CreateNewCatalogue from '../../shared/create-new-catalogue';
import { IndividualFilter } from '../../shared/individual-filter.enum';
import CatalogueBar from '../catalogue-bar';
import { Catalogues } from '../catalogue-constants.enum';
import CatalogueLink from '../catalogue-link';
import {
  displaySaleType,
  formatDate,
  getTableHeaders,
  NoCataloguesMessage,
  NoCataloguesMessageDescription,
} from '../catalogue-utils';
import { DeleteCatalogue } from '../delete-catalogue/delete-catalogue';
import { EditCatalogue } from '../edit-catalogue/edit-catalogue';
import { NoCatalogues } from '../no-catalogues/no-catalogues';
import { PenCardsButton } from '../pen-cards/pen-cards-button/pen-cards-button';
import { MyCataloguesProps } from './my-catalogues-props';
import './my-catalogues.scss';

/**
 * List of sales catalogues.
 * @param props
 */
export const MyCatalogues: React.FC<MyCataloguesProps> = (props) => {
  const {
    individual, updateIndividualFilters,
  } = useFilters();

  const {
    primeFilters, updatePrime,
  } = usePrime();
  const {
    userType, db,
  } = primeFilters;

  const navigation = useNavigation();
  const [defaultDatabase] = useState(getDefaultDatabase(db.id, databaseTypes));
  const [saleData, setsaleData] = useState<Catalogue[]>([]);
  const [semenData, setsemenData] = useState<Catalogue[]>([]);
  const [salesColumns, setSalesColumns] = useState<Column[]>([]);
  const [semenColumns, setSemenColumns] = useState<Column[]>([]);
  const [saleSort, setSaleSort] = useState<CatalogueSort>({
    sortBy: CatalogueSortBy.SaleDate,
    sortDir: CatalogueSortDir.asc,
  });
  const [semenSort, setSemenSort] = useState<CatalogueSort>({
    sortBy: CatalogueSortBy.Title,
    sortDir: CatalogueSortDir.desc,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [breederId, setBreederId] = useState<string>(props.breederId);
  const [breederName, setBreederName] = useState<string>(props.breederName);
  const [breederChoice, setBreederChoice] = useState<DropdownItem[]>([{ id: 'loading', value: 'loading' }]);

  const [databaseSelected, setDatabaseSelected] = useState<boolean>(false);

  const handleBreederListSelection = (bId: string, bName: string) => {
    setBreederId(bId);
    setBreederName(bName);
    navigation.navigate(`/catalogue/my?user=${userType.id}&db=${db.id}&breederId=${bId}&breederName=${bName}`);
  };
  /**
   * Updates the filters state when a filter value changes.
   * @param filter The filter to update.
   * @param value The new value from the filter.
   */
  const filterChange = useCallback((filter: IndividualFilter, value: any) => {
    const filterValue = individual.get(filter);
    if (deepEqual(filterValue, value)) {
      return;
    }
    const individualFilters = new Map(individual);
    const val = isNullOrUndefined(value) ? '' : value;
    individualFilters.set(filter, val);
    updateIndividualFilters(individualFilters);
    handleBreederListSelection(val.id, val.value);
  }, [handleBreederListSelection, individual, updateIndividualFilters]);

  /**
   * Calls api with selected attributes to get catalogues
   */
  const loadBreederList = useCallback(async (source: CancelTokenSource) => CatalogueApi
    .getBreederList(source.token), []);
  const loadResults = useCallback(async (
    bId: string,
    catalogueType: CatalogueType,
    sorting: CatalogueSort,
    source: CancelTokenSource,
  ) => CatalogueApi.getMyCatalogueList(bId, catalogueType, sorting, db.id, source.token), [db]);

  /**
   * Sorts selected catalogues
   * @param sortBy sortBy
   * @param sortDir sorting direction
   * @param catalogueType type of the catalogue
   */
  const sortResult = async (
    sortBy: CatalogueSortBy,
    sortDir: CatalogueSortDir,
    catalogueType: CatalogueType,
  ) => {
    const source = Axios.CancelToken.source();
    setLoading(true);

    const sortOption = {
      sortBy,
      sortDir,
    };

    const d = await loadResults(breederId, catalogueType, sortOption, source);

    setTimeout(() => {
      if (catalogueType === CatalogueType.Semen) {
        setsemenData(d);
        setSemenSort(sortOption);
      } else {
        setsaleData(d);
        setSaleSort(sortOption);
      }

      setLoading(false);
    }, ResultsNumConstants.Delay);

    setLoading(false);
  };

  const disableEdit = (catalogueType: CatalogueType, catalogue: Catalogue) => {
    let disabled = false;
    const date = moment().startOf('day');

    switch (catalogueType) {
      case CatalogueType.Sales:
        // Editing sales catalogues is disabled if the sale end date has passed.
        disabled = catalogue.isActive && date.isAfter(moment(catalogue.saleEndDate));
        break;
      case CatalogueType.Semen:
        disabled = false;
        break;
      default:
    }

    return disabled;
  };
  /**
   * Delete catalogue and reload the page
   * @param id
   * @param catalogueType
   */
  const deleteCatalogue = async (id: number, catalogueType: CatalogueType) => {
    const source = Axios.CancelToken.source();
    setLoading(true);
    await catalogueApi.delete(id, source.token);
    const sortOption = catalogueType === CatalogueType.Sales ? saleSort : semenSort;
    const d = await loadResults(breederId, catalogueType, sortOption, source);

    if (catalogueType === CatalogueType.Semen) {
      setsemenData(d);
    } else {
      setsaleData(d);
    }

    setLoading(false);
  };

  const updateDB = (item: CascadingMenuItem) => {
    // updates database filter state
    const newDb = {
      value: item.value,
      id: item.id,
    };
    const dbFilters = { db: newDb };
    updatePrime(dbFilters);
    setDatabaseSelected(true);
    // Saving to local storage even if trait or search type wasn't selected.
    // It's needed when 'back to dashboard' from catalogues to populate selected options.
    addToStorage(StorageKey.Prime, { userType, ...dbFilters });
    navigation.navigate(`/catalogue/my?user=${userType.id}&db=${newDb.id}&breederId=${breederId}&breederName=${breederName}`);
  };

  /**
   * Get breederList
   */
  useEffect(() => {
    const source = Axios.CancelToken.source();
    setLoading(true);

    // preset admin autocomplete with email if provide
    const individualFilters = new Map(individual);
    individualFilters.set(IndividualFilter.BreederId, { id: breederId, value: breederName });
    updateIndividualFilters(individualFilters);

    const getBreederList = async () => {
      const d = await loadBreederList(source);
      if (d.length) {
        setBreederChoice(d.map((breeder) => ({ id: breeder.userId, value: `${breeder.firstName} ${breeder.lastName} / ${breeder.email}` })));
      } else {
        setBreederChoice([{ id: '', value: 'No permissions' }]);
      }
      setLoading(false);
    };
    getBreederList();

    return () => {
      source.cancel();
    };
  }, []);

  /**
   * Get catalogues
   */
  useEffect(() => {
    const source = Axios.CancelToken.source();
    setLoading(true);

    const initialSort = {
      sortBy: CatalogueSortBy.SaleDate,
      sortDir: CatalogueSortDir.asc,
    };

    const getCatalogues = async () => {
      const d = await loadResults(breederId, CatalogueType.Sales, initialSort, source);
      setsaleData(d);
      setLoading(false);
    };
    getCatalogues();

    return () => {
      source.cancel();
    };
  }, [breederId, db, loadResults]);

  /**
   * Initial Semen catalogue retrieve
   */
  useEffect(() => {
    const source = Axios.CancelToken.source();
    setLoading(true);

    const initialSort = {
      sortBy: CatalogueSortBy.Title,
      sortDir: CatalogueSortDir.desc,
    };

    const getCatalogues = async () => {
      const d = await loadResults(breederId, CatalogueType.Semen, initialSort, source);
      setsemenData(d);
    };
    getCatalogues();

    return () => {
      source.cancel();
    };
  }, [breederId, db, loadResults]);

  /**
   * Sets columns from retrieved sales catalogues
   */
  useEffect(() => {
    if (saleData.length) {
      const cols = getTableHeaders(Catalogues.MySalesCatalogues, saleSort.sortBy, saleSort.sortDir);
      if (!deepEqual(salesColumns, cols)) {
        setSalesColumns(cols);
      }
    }
  }, [saleData, saleSort, salesColumns]);

  /**
   * Sets columns from retrieved Semen catalogues
   */
  useEffect(() => {
    if (semenData.length) {
      const cols = getTableHeaders(
        Catalogues.MySemenCatalogues,
        semenSort.sortBy,
        semenSort.sortDir,
      );
      if (!deepEqual(semenColumns, cols)) {
        setSemenColumns(cols);
      }
    }
  }, [semenColumns, semenData, semenSort]);

  return (
    <>
      <Hidden xsDown>
        <BackToDashboard />
        <CatalogueBar title={Catalogues.MyCatalogues} />
        <AuthorizedContent
          roles={[UserRole.Admin, UserRole.ServiceProvider, UserRole.Breeder]}
        >
          <Container className="sg-catalogues sg-catalogues__impersonate">
            <Grid
              item
              xs={4}
            >
              <div className="container-padding">
                <AuthorizedContent
                  roles={[UserRole.Admin]}
                >
                  <Autocomplete
                    items={[{ id: breederId, value: breederName }]}
                    item={individual.get(IndividualFilter.BreederId)}
                    value={individual.get(IndividualFilter.BreederId)?.value}
                    label={`${IndividualConstants.BreederLabel}:`}
                    placeholder={IndividualConstants.BreederPlaceholder}
                    noResultsText={IndividualConstants.BreederNoResults}
                    getItems={(keyword, token) => getBreederAdmin(keyword, token)}
                    onSelectionChange={(item) => handleBreederListSelection(item.length ? item[0].id.toString() : '', item.length ? item[0].value : '')}
                  />
                </AuthorizedContent>
                <AuthorizedContent
                  roles={[UserRole.ServiceProvider]}
                >
                  <Dropdown
                    label="Select a Breeder to impersonate"
                    placeholder={breederName ?? 'Breeder\'s Email'}
                    items={breederChoice}
                    onSelectionChange={(item) => handleBreederListSelection(
                      item[0].id.toString(),
                      item[0].value,
                    )}
                  />
                </AuthorizedContent>
              </div>
            </Grid>
            <Grid
              item
              xs={4}
            >
              <div className="container-padding">
                <span className="prime__dropdown">
                  <InputLabel>Select Analysis</InputLabel>
                  <CascadingMenu
                    id="sg-dropdown-db"
                    items={databaseTypes}
                    selected={(val) => updateDB(val)}
                  // default={defaultDatabase?.value}
                  />
                </span>
              </div>
            </Grid>
          </Container>
        </AuthorizedContent>
        {databaseSelected && (
          <div className="sg-catalogues sg-my-catalogues">
            <Container className="sg-catalogues__info">
              <div className="container-padding">
                <h3>{Catalogues.Sales} {breederName}</h3>
              </div>
            </Container>
            <Container className="sg-catalogues__content sg-my-catalogues">

              {loading ? (<div className="sg-catalogues__indicator"><CircularProgress color="primary" /></div>) : (
                <>
                  {saleData.length ? (
                    <Table
                      columns={salesColumns}
                      isVerticallyScrolled
                      scrollShadow
                      stickyHeader
                      totalCount={saleData.length}
                      resultsCount={saleData.length}
                      loading={loading}
                      loadingMore={false}
                      enableSort
                      sortBy="id"
                      onHeaderSelected={(col, dir) => sortResult(col, dir, CatalogueType.Sales)}
                    >
                      {saleData.map((catalogue: Catalogue, index: number) => (
                        <TableFade
                          in
                          customDelay={(index % 20) * 50}
                          key={catalogue.id}
                        >
                          <TableRow
                            className="sg-table__row"
                          >
                            <TableCell className="cell sticky small" />
                            <TableCell className="cell sg-my-catalogues__title">
                              <div>{catalogue.isActive
                                ? (
                                  <CatalogueLink
                                    db={props.db}
                                    user={props.user}
                                    trait={props.trait}
                                    asbvs={props.asbvs}
                                    label={catalogue.title}
                                    id={catalogue.id}
                                    type={CatalogueType.Sales}
                                  />
                                )
                                : (<span>{catalogue.title}</span>)}
                              </div>
                              <Chip
                                label={catalogue.isActive ? 'Published' : 'Draft'}
                                size="small"
                                color="default"
                                className={`sg-my-catalogues__chip ${catalogue.isActive ? 'sg-my-catalogues__chip--published' : ''}`}
                                clickable={false}
                              />
                            </TableCell>
                            <TableCell
                              className="cell sg-my-catalogues__date"
                              dangerouslySetInnerHTML={{
                                __html: formatDate(
                                  catalogue.listingStartDateDesc,
                                  catalogue.listingEndDateDesc,
                                ),
                              }}
                            />
                            <TableCell className="cell sg-my-catalogues__listed">{catalogue.listedAnimals}</TableCell>
                            <TableCell
                              className="cell sg-my-catalogues__date"
                              dangerouslySetInnerHTML={{
                                __html: formatDate(
                                  catalogue.saleStartDateDesc,
                                  catalogue.saleEndDateDesc,
                                ),
                              }}
                            />
                            <TableCell className="cell sg-my-catalogues__type">{displaySaleType(catalogue.saleTypeDesc)}</TableCell>
                            <TableCell className="cell sticky">
                              <div className="sg-my-catalogues__actions">
                                <PenCardsButton
                                  id={catalogue.id}
                                  db={props.db}
                                  trait={props.trait}
                                  user={props.user}
                                  breederId={props.breederId}
                                  breederName={props.breederName}
                                  disabled={!catalogue.listedAnimals}
                                />
                                <EditCatalogue
                                  id={catalogue.id}
                                  type={catalogue.saleType}
                                  disabled={disableEdit(CatalogueType.Sales, catalogue)}
                                />
                                <DeleteCatalogue
                                  id={catalogue.id}
                                  type={CatalogueType.Sales}
                                  onDelete={deleteCatalogue}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableFade>
                      ))}
                    </Table>
                  ) : (
                    <NoCatalogues
                      mainMessage={NoCataloguesMessage.NoSalesCataloguesTitle}
                      description={NoCataloguesMessageDescription.CreateNewSaleCatalogue}
                    />
                  )}
                  <AuthorizedContent roles={[UserRole.ServiceProvider]}>
                    <CreateNewCatalogue
                      type={SaleType.SingleVendorAuction}
                      disabled={!breederId}
                    />
                  </AuthorizedContent>
                  <AuthorizedContent roles={[UserRole.Breeder, UserRole.Admin]}>
                    <CreateNewCatalogue type={SaleType.SingleVendorAuction} />
                  </AuthorizedContent>
                  <Divider variant="fullWidth" />
                </>
              )}
              {!loading && (
                <div className="sg-my-catalogues__semen">
                  <Container className="sg-catalogues__info">
                    <div>
                      <h3>{Catalogues.Semen} {breederName}</h3>
                    </div>
                  </Container>
                  {semenData.length ? (
                    <Table
                      columns={semenColumns}
                      isVerticallyScrolled
                      scrollShadow
                      stickyHeader
                      totalCount={semenData.length}
                      resultsCount={semenData.length}
                      loading={false}
                      loadingMore={false}
                      enableSort
                      onHeaderSelected={(col, dir) => sortResult(col, dir, CatalogueType.Semen)}
                      showLoadMore={false}
                      showHeader
                      className="sg-my-catalogues"
                      sortBy="id"
                    >
                      {semenData.map((catalogue: Catalogue, index: number) => (
                        <TableFade
                          in
                          customDelay={(index % 20) * 50}
                          key={catalogue.id}
                        >
                          <TableRow
                            className="sg-table__row"
                          >
                            <TableCell className="cell sticky small" />
                            <TableCell className="cell sg-my-catalogues__title">
                              <div>{catalogue.isActive
                                ? (
                                  <CatalogueLink
                                    db={props.db}
                                    user={props.user}
                                    trait={props.trait}
                                    asbvs={props.asbvs}
                                    label={catalogue.title}
                                    id={catalogue.id}
                                    type={CatalogueType.Semen}
                                  />
                                )
                                : (<span>{catalogue.title}</span>)}
                              </div>
                              <Chip
                                label={catalogue.isActive ? 'Published' : 'Draft'}
                                size="small"
                                color="default"
                                className={`sg-my-catalogues__chip ${catalogue.isActive ? 'sg-my-catalogues__chip--published' : ''}`}
                                clickable={false}
                              />
                            </TableCell>
                            <TableCell className="cell sg-my-catalogues__semen__listed">{catalogue.listedAnimals}</TableCell>
                            <TableCell className="cell sticky">
                              <div className="sg-my-catalogues__actions">
                                <PenCardsButton
                                  id={catalogue.id}
                                  db={props.db}
                                  trait={props.trait}
                                  user={props.user}
                                  disabled={!catalogue.listedAnimals}
                                />
                                <EditCatalogue
                                  id={catalogue.id}
                                  type={catalogue.saleType}
                                />
                                <DeleteCatalogue
                                  id={catalogue.id}
                                  type={CatalogueType.Semen}
                                  onDelete={deleteCatalogue}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableFade>
                      ))}
                    </Table>
                  ) : (
                    <NoCatalogues
                      mainMessage={NoCataloguesMessage.NoSalesCataloguesTitle}
                      description={NoCataloguesMessageDescription.CreateNewSemenCatalogue}
                    />
                  )}
                  <AuthorizedContent roles={[UserRole.ServiceProvider]}>
                    <CreateNewCatalogue
                      type={SaleType.AllSemenCatalogue}
                      disabled={!breederId}
                    />
                  </AuthorizedContent>
                  <AuthorizedContent roles={[UserRole.Breeder, UserRole.Admin]}>
                    <CreateNewCatalogue type={SaleType.AllSemenCatalogue} />
                  </AuthorizedContent>
                </div>
              )}
            </Container>
          </div>
        )}
      </Hidden>
    </>
  );
};

(MyCatalogues as any).whyDidYouRender = true;
